// Vendored from cube-js/cube@d403e7a0 packages/cubejs-api-gateway/src/date-parser.ts, Apache-2.0 (Copyright Cube Dev, Inc.).
// Changed: every branch reads `now` rather than the clock; Number replaces parseInt and destructuring replaces indexing, for this repo's lint and strict compilers.

import moment from "moment-timezone";
import { type ParsedComponents, parse } from "chrono-node";

import { UserError } from "./user-error";

const momentFromResult = (
  result: ParsedComponents,
  timezone: string,
): moment.Moment => {
  const dateMoment = moment().tz(timezone);

  dateMoment.set("year", result.get("year") as number);
  dateMoment.set("month", (result.get("month") as number) - 1);
  dateMoment.set("date", result.get("day") as number);
  dateMoment.set("hour", result.get("hour") as number);
  dateMoment.set("minute", result.get("minute") as number);
  dateMoment.set("second", result.get("second") as number);
  dateMoment.set("millisecond", result.get("millisecond") as number);

  return dateMoment;
};

export function dateParser(
  dateString: string,
  timezone: string,
  now: Date = new Date(),
): string[] {
  let momentRange: moment.Moment[];
  dateString = dateString.toLowerCase();

  const relMatch = dateString.match(
    /(this|last|next)\s+(day|week|month|year|quarter|hour|minute|second)/,
  );
  const relNMatch = relMatch
    ? null
    : dateString.match(
        /(last|next)\s+(\d+)\s+(day|week|month|year|quarter|hour|minute|second)/,
      );

  if (relMatch) {
    const unit = relMatch[2] as moment.unitOfTime.DurationConstructor &
      moment.unitOfTime.StartOf;
    let start = moment(now).tz(timezone);
    let end = moment(now).tz(timezone);
    if (relMatch[1] === "last") {
      start = start.add(-1, unit);
      end = end.add(-1, unit);
    }
    if (relMatch[1] === "next") {
      start = start.add(1, unit);
      end = end.add(1, unit);
    }

    const span = unit === "week" ? "isoWeek" : unit;
    momentRange = [start.startOf(span), end.endOf(span)];
  } else if (relNMatch) {
    const unit = relNMatch[3] as moment.unitOfTime.DurationConstructor &
      moment.unitOfTime.StartOf;

    let start = moment(now).tz(timezone);
    let end = moment(now).tz(timezone);
    if (relNMatch[1] === "last") {
      start = start.add(-Number(relNMatch[2]), unit);
      end = end.add(-1, unit);
    }
    if (relNMatch[1] === "next") {
      start = start.add(1, unit);
      end = end.add(Number(relNMatch[2]), unit);
    }

    const span = unit === "week" ? "isoWeek" : unit;
    momentRange = [start.startOf(span), end.endOf(span)];
  } else if (dateString.match(/today/)) {
    momentRange = [
      moment(now).tz(timezone).startOf("day"),
      moment(now).tz(timezone).endOf("day"),
    ];
  } else if (dateString.match(/yesterday/)) {
    momentRange = [
      moment(now).tz(timezone).startOf("day").add(-1, "day"),
      moment(now).tz(timezone).endOf("day").add(-1, "day"),
    ];
  } else if (dateString.match(/tomorrow/)) {
    momentRange = [
      moment(now).tz(timezone).startOf("day").add(1, "day"),
      moment(now).tz(timezone).endOf("day").add(1, "day"),
    ];
  } else if (dateString.match(/^from (.*) to (.*)$/)) {
    const match = dateString.match(/^from(.{0,50})to(.{0,50})$/);
    if (!match) {
      throw new UserError(`Can't parse date range: '${dateString}'`);
    }

    let [, from = "", to = ""] = match;
    from = from.trim();
    to = to.trim();

    const current = moment(now).tz(timezone);
    const fromResults = parse(
      from.trim(),
      new Date(current.format(moment.HTML5_FMT.DATETIME_LOCAL_MS)),
    );
    const toResults = parse(
      to.trim(),
      new Date(current.format(moment.HTML5_FMT.DATETIME_LOCAL_MS)),
    );

    const [fromResult] = fromResults;
    const [toResult] = toResults;
    if (!fromResult) {
      throw new UserError(`Can't parse date: '${from}'`);
    }

    if (!toResult) {
      throw new UserError(`Can't parse date: '${to}'`);
    }

    const exactGranularity: moment.unitOfTime.StartOf =
      (["second", "minute", "hour"] as const).find(
        (g) => dateString.indexOf(g) !== -1,
      ) || "day";
    momentRange = [
      momentFromResult(fromResult.start, timezone).startOf(exactGranularity),
      momentFromResult(toResult.start, timezone).endOf(exactGranularity),
    ];
  } else {
    const current = moment(now).tz(timezone);
    const results = parse(
      dateString,
      new Date(current.format(moment.HTML5_FMT.DATETIME_LOCAL_MS)),
    );

    const [result] = results;
    if (!result) {
      throw new UserError(`Can't parse date: '${dateString}'`);
    }

    const exactGranularity: moment.unitOfTime.StartOf =
      (["second", "minute", "hour"] as const).find(
        (g) => dateString.indexOf(g) !== -1,
      ) || "day";
    momentRange = [
      momentFromResult(result.start, timezone).startOf(exactGranularity),
      momentFromResult(result.end ?? result.start, timezone).endOf(
        exactGranularity,
      ),
    ];
  }

  return momentRange.map((d) => d.format(moment.HTML5_FMT.DATETIME_LOCAL_MS));
}
